import {
  CheckIcon,
  FormControlLabel,
  GKButton,
  GKDatePicker,
  GKModal,
  GKSearchSelect,
  GKTextArea,
  GKTextField,
  Grid2,
  NumberIcon,
  Show,
  Switch,
  Typography,
} from "components";
import dayjs from "dayjs";
import {
  CalCulationHistory,
  CalculationRowData,
  ProformaFormValue,
} from "interfaces/Revenue";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateProformaMutation } from "store/features/Revenue/FeeCalculationApi";
import { proformaApi } from "store/features/Revenue/ProformaApi";
import { useAppDispatch } from "store/store";
import { GKModalProps } from "utils/commonInterface";
import { stateList } from "utils/stateJSON";
import { validateNotWhitespaceOnly } from "utils/validationSchema";

interface Props {
  calculationRowData: CalculationRowData | CalCulationHistory;
  isDirectProforma?: boolean;
}

const ProformaModal = (props: GKModalProps & Props) => {
  const { open, setOpen, calculationRowData, isDirectProforma = false } = props;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const form = useForm<ProformaFormValue>({
    defaultValues: {
      clientState: "",
      advisorState: "",
      proformaDate: "",
      proformaNumber: "",
      includeGst: false,
      remarks: "Thanks for your business.",
    },
  });

  const watchIncludeGst = form.watch("includeGst");

  const [proformaCreate, { isLoading }] = useCreateProformaMutation();

  return (
    <GKModal
      modalTitle={"Proforma Invoice Details"}
      width={"570px"}
      open={open}
      setOpen={setOpen}
      footer={
        <Grid2
          container
          size={12}
          display={"flex"}
          justifyContent={"space-between"}
        >
          <Controller
            name="includeGst"
            control={form.control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                sx={{ gap: 1 }}
                control={
                  <Switch {...field} checked={field.value} color="primary" />
                }
                label={
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="var(--grey-900)"
                  >
                    Include GST
                  </Typography>
                }
                labelPlacement="start"
              />
            )}
          />
          <GKButton
            type="submit"
            form="create-proforma-form"
            variant="contained"
            startIcon={<CheckIcon />}
            loading={isLoading}
          >
            Save & Create Proforma
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="create-proforma-form"
        onSubmit={form.handleSubmit(async (values: ProformaFormValue) => {
          let data = values;

          if (isDirectProforma) {
            data = {
              ...data,
              calculationData: calculationRowData,
            };
          }

          try {
            await proformaCreate({
              data: data,
              isDirectProforma,
              calculationId: calculationRowData?.id,
            }).unwrap();
            dispatch(proformaApi.util.invalidateTags(["ProformaList"]));

            if (isDirectProforma) navigate(-1);

            setOpen(false);
          } catch (err) {
            console.error("Create proforma failed:", err);
          }
        })}
      >
        <Grid2 container spacing={3}>
          <Show.When isTrue={watchIncludeGst}>
            <Grid2 size={6}>
              <Controller
                name="advisorState"
                control={form.control}
                rules={{
                  required: {
                    value: watchIncludeGst,
                    message: "Please select Advisor state",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    requiredField
                    inputLabel="Advisor State"
                    options={stateList || []}
                    value={
                      stateList.find((item) => item.name === field.value)?.name
                    }
                    onChange={(e, val) => field.onChange(val?.name)}
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="clientState"
                control={form.control}
                rules={{
                  required: {
                    value: watchIncludeGst,
                    message: "Please select client state",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    requiredField
                    inputLabel="Client State"
                    options={stateList || []}
                    value={
                      stateList.find((item) => item.name === field.value)?.name
                    }
                    onChange={(e, val) => field.onChange(val?.name)}
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
          </Show.When>

          <Grid2 size={6}>
            <Controller
              name="proformaNumber"
              control={form.control}
              rules={{
                required: "Invoice number is required",
                minLength: {
                  value: 1,
                  message: "Minimum length is 1 characters",
                },
                maxLength: {
                  value: 49,
                  message: "Maximum length is 49 characters",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Proforma Invoice No."
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    input: { endAdornment: <NumberIcon /> },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="proformaDate"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select proforma invoice date",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKDatePicker
                  {...field}
                  requiredField
                  inputLabel="Proforma Invoice Date"
                  value={dayjs(field.value)}
                  onChange={(value) => {
                    field.onChange(dayjs(value).format("YYYY-MM-DD"));
                  }}
                  disableFuture
                  slotProps={{
                    textField: { error: !!error, helperText: error?.message },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="remarks"
              control={form.control}
              render={({ field }) => (
                <GKTextArea
                  {...field}
                  inputLabel="Remarks"
                  defaultValue={"Thanks for your business."}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default ProformaModal;
